import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import BeatLoader from "react-spinners/BeatLoader";
import { toast } from "react-toastify";
import { NAC_STATUSES, NacApiError, nacRequest } from "../services/nacService";

export const STATUS_STYLE: Record<string, string> = {
  BOZZA: "bg-gray-200 text-gray-700",
  "DA CONFERMARE": "bg-yellow-100 text-yellow-800",
  INVIATA: "bg-blue-100 text-blue-800",
  "IN LAVORAZIONE": "bg-indigo-100 text-indigo-800",
  LAVORATA: "bg-green-100 text-green-800",
  ARCHIVIATA: "bg-green-200 text-green-900",
};

export const StatusBadge = ({ status }: { status: string }) => (
  <span className={classNames("inline-block rounded px-2 py-1 text-xs font-semibold", STATUS_STYLE[status] ?? "bg-gray-100")}>
    {status}
  </span>
);

export const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

type NacRow = {
  id: string;
  nac_code: string | null;
  status: string;
  ragione_sociale: string | null;
  partita_iva: string | null;
  codice_fiscale: string | null;
  tipologia_soggetto: string;
  created_at: string;
  codice_cliente: string | null;
  agent_name: string | null;
  ambiguous_match: boolean;
  email_failed: boolean;
  can_edit: boolean;
  can_delete: boolean;
};

const SORT_FIELDS = [
  { value: "ragione_sociale", label: "Ragione Sociale" },
  { value: "created_at", label: "Data" },
  { value: "status", label: "Stato" },
  { value: "nac_code", label: "NAC" },
];

export default function Nac() {
  const [rows, setRows] = useState<NacRow[]>([]);
  const [supervisor, setSupervisor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [sort, setSort] = useState({ field: "", dir: "ASC" });
  const [sortDraft, setSortDraft] = useState({ field: "", dir: "ASC" });
  const [reload, setReload] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const deleteDraft = async (r: NacRow) => {
    if (!window.confirm(`Eliminare la bozza${r.ragione_sociale ? ` "${r.ragione_sociale}"` : ""}? L'operazione non può essere annullata.`)) return;
    setDeletingId(r.id);
    try {
      await nacRequest(`/drafts/${r.id}`, "DELETE");
      toast.success("Bozza eliminata", { position: "top-right", className: "m-5" });
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e instanceof NacApiError ? e.message : "Errore durante l'eliminazione", { position: "top-right", className: "m-5" });
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (status) qs.set("status", status);
        if (search.trim()) qs.set("search", search.trim());
        const data = await nacRequest(`?${qs.toString()}`);
        if (!cancelled) {
          setRows(data.requests ?? []);
          setSupervisor(!!data.supervisor);
        }
      } catch (e) {
        if (!cancelled) toast.error(e instanceof NacApiError ? e.message : "Errore nel caricamento delle richieste", { position: "top-right", className: "m-5" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [status, search, reload]);

  const applyFilters = () => {
    setSearch(searchInput);
    setStatus(statusDraft);
    setSort(sortDraft);
  };

  const clearFilters = () => {
    setSearchInput("");
    setStatusDraft("");
    setSortDraft({ field: "", dir: "ASC" });
    setSearch("");
    setStatus("");
    setSort({ field: "", dir: "ASC" });
  };

  const sortedRows = useMemo(() => {
    if (!sort.field) return rows;
    const val = (r: NacRow) => String(r[sort.field as keyof NacRow] ?? "").toLowerCase();
    const out = [...rows].sort((a, b) => val(a).localeCompare(val(b), "it", { numeric: true }));
    return sort.dir === "DESC" ? out.reverse() : out;
  }, [rows, sort]);

  const filterButtons = [{ value: "", label: "Tutti" }, ...NAC_STATUSES.map((s) => ({ value: s, label: s }))];

  const filtersPanel = (
    <div>
      <div className="flex items-center justify-between">
        <h4 className="text-xl text-blue-600">Filtri</h4>
        <button type="button" onClick={clearFilters} className="text-xs text-blue-600 underline">Azzera</button>
      </div>
      <div className="flex items-center py-3 text-neutral-600 after:ms-6 after:flex-[1_1_0%] after:border-t after:border-gray-600 dark:text-white">
        Stato
      </div>
      <select
        className="w-full border border-neutral-300 px-2 py-2 text-sm"
        value={statusDraft}
        onChange={(e) => setStatusDraft(e.target.value)}
      >
        {filterButtons.map((b) => (
          <option key={b.value || "tutti"} value={b.value}>{b.label}</option>
        ))}
      </select>
      <button type="button" onClick={applyFilters} className="mt-6 bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700">
        Filtra
      </button>
    </div>
  );

  const sortPanel = (
    <div>
      <h4 className="text-xl text-blue-600">Ordina per</h4>
      <div className="mt-12 flex gap-2">
        <select
          className="w-full border border-neutral-300 px-2 py-2 text-sm"
          value={sortDraft.field}
          onChange={(e) => setSortDraft({ ...sortDraft, field: e.target.value })}
        >
          <option value="">+ Ordinamento</option>
          {SORT_FIELDS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <button
          type="button"
          title={sortDraft.dir === "ASC" ? "Crescente" : "Decrescente"}
          onClick={() => setSortDraft({ ...sortDraft, dir: sortDraft.dir === "ASC" ? "DESC" : "ASC" })}
          className="material-symbols-outlined text-xl text-blue-600"
        >
          {sortDraft.dir === "ASC" ? "arrow_downward" : "arrow_upward"}
        </button>
      </div>
      <button type="button" onClick={applyFilters} className="mt-6 bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700">
        Filtra
      </button>
    </div>
  );

  const results = loading ? (
    <div className="py-10 text-center"><BeatLoader color="#1d4ed8" /></div>
  ) : sortedRows.length === 0 ? (
    <p className="py-10 text-center text-gray-500">Nessuna richiesta trovata.</p>
  ) : (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="border-b text-xs uppercase text-gray-600">
          <tr>
            <th className="p-3">NAC</th>
            <th className="p-3">Cliente</th>
            <th className="p-3">P. IVA / C.F.</th>
            {supervisor && <th className="p-3">Agente</th>}
            <th className="p-3">Data</th>
            <th className="p-3">Stato</th>
            <th className="p-3">Codice cliente</th>
            <th className="p-3 text-right">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => (
            <tr key={r.id} className="border-b hover:bg-gray-50">
              <td className="p-3 font-mono">
                <Link className="text-blue-700 underline" to={`/nac/${r.id}`}>{r.nac_code ?? "Bozza"}</Link>
              </td>
              <td className="p-3">{r.ragione_sociale || "—"}</td>
              <td className="p-3">{(r.tipologia_soggetto === "Privato" ? r.codice_fiscale : r.partita_iva) || "—"}</td>
              {supervisor && <td className="p-3">{r.agent_name}</td>}
              <td className="p-3">{formatDate(r.created_at)}</td>
              <td className="p-3">
                <StatusBadge status={r.status} />
                {r.ambiguous_match && <span className="ml-2 text-xs text-red-700" title="Più clienti AS400 con la stessa chiave: apri la richiesta e scegli il codice cliente">match ambiguo</span>}
              </td>
              <td className="p-3 font-mono">{r.codice_cliente ?? "—"}</td>
              <td className="p-3">
                <div className="flex justify-end gap-2">
                  <Link className="border px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100" to={`/nac/${r.id}`}>
                    Dettagli
                  </Link>
                  {r.can_edit && (
                    <Link className="border border-blue-700 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50" to={`/nac/${r.id}`}>
                      Modifica
                    </Link>
                  )}
                  {r.can_delete && (
                    <button
                      type="button"
                      disabled={deletingId === r.id}
                      onClick={() => deleteDraft(r)}
                      className="border border-red-700 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Elimina
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="px-5 pb-24 pt-28 sm:px-12">
      <div className="mx-auto mb-12 flex flex-col items-center sm:max-w-4xl">
        <h2 className="mb-8 text-4xl font-bold text-blue-500">Nuova Anagrafica Clienti</h2>
        <div className="flex w-full flex-col items-stretch gap-4 md:flex-row md:items-center md:justify-center">
          <input
            className="w-full border border-neutral-300 px-3 py-2 md:w-96"
            placeholder="Cerca per nome, P. IVA, codice fiscale o NAC"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
          />
          <button type="button" onClick={applyFilters} className="bg-blue-600 px-5 py-2 font-semibold text-white hover:bg-blue-700">
            Cerca
          </button>
          <Link to="/nac/new" className="bg-blue-600 px-5 py-2 text-center font-semibold text-white hover:bg-blue-700">
            + Nuova richiesta NAC
          </Link>
        </div>
      </div>

      <hr className="border-neutral-300" />

      <div className="mt-8 flex flex-col gap-4 lg:flex-row">
        <div className="w-full lg:w-1/5 lg:px-4">{filtersPanel}</div>
        <div className="w-full min-w-0 lg:w-3/5">{results}</div>
        <div className="w-full lg:w-1/5 lg:px-4">{sortPanel}</div>
      </div>
    </div>
  );
}
